import React, { useState, useEffect } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import {
  Row,
  Col,
  Card,
  Statistic,
  Select,
  DatePicker,
  Space,
  Alert,
  Spin,
  Typography,
  Button,
  Grid,
  Table,
  Tabs,
  Tag,
  Progress,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  TrophyOutlined,
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TabletOutlined,
  LaptopOutlined,
  DesktopOutlined,
  MobileOutlined,
  WifiOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { dashboardAPI, analyticsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useViewMode } from '../hooks/useViewMode';
import { useFullscreen } from '../contexts/FullscreenContext';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const { TabPane } = Tabs;

interface KPICardData {
  title: string;
  value: string;
  target?: string;
  achievement?: number;
  achievement_text?: string;
  change_percent?: number;
  trend?: string;
  color?: string;
  category?: string;
}

interface PromotionData {
  week_date: string;
  amazon_promotion: number;
  shopify_promotion: number;
  total_promotion: number;
}

interface ChartData {
  labels: string[];
  datasets: any[];
  promotions?: PromotionData[];
}

interface AlertData {
  type: string;
  title: string;
  message: string;
  severity: string;
}

interface DashboardData {
  kpi_cards: KPICardData[];
  sales_trend: ChartData;
  channel_distribution: ChartData;
  top_products: ChartData;
  alerts: AlertData[];
}

interface ProductTableData {
  sku_code: string;
  sku_name: string;
  sell_in_sales: number;
  sell_through_sales: number;
  pos_sales: number;
  installation_count: number;
  sell_through_rate: number;
  sales_records: number;
}

interface SalesTableData {
  week_date: string;
  channel_name: string;
  sell_in_sales: number;
  sell_through_sales: number;
  pos_sales: number;
  installation_count: number;
  sell_in_units: number;
  sell_through_units: number;
  pos_units: number;
  sell_through_rate: number;
}

const Dashboard: React.FC = () => {
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [channelPerformanceData, setChannelPerformanceData] = useState<any>(null);
  const [channelPerformanceLoading, setChannelPerformanceLoading] = useState(false);
  const [channelRankingData, setChannelRankingData] = useState<any[]>([]);
  const [channelRankingLoading, setChannelRankingLoading] = useState(false);
  const [skuRankingData, setSkuRankingData] = useState<any[]>([]);
  const [skuRankingLoading, setSkuRankingLoading] = useState(false);
  const [visibleChannels, setVisibleChannels] = useState<{[key: string]: boolean}>({});
  const [selectedChannel, setSelectedChannel] = useState<string>('All');

  // Channel name abbreviation mapping for Matrix scatter plot
  const getChannelAbbreviation = (channelName: string): string => {
    const abbreviations: {[key: string]: string} = {
      'Home Depot': 'HD',
      'HOME_DEPOT': 'HD',
      'Amazon': 'AM',
      'AMAZON': 'AM',
      'BestBuy Canada': 'BC',
      'BESTBUY': 'BB',
      'BESTBUY_CANADA': 'BC',
      'Home Depot Canada': 'HC',
      'HOME_DEPOT_CANADA': 'HC',
      'ProMrkt': 'PM',
      'PROMRKT': 'PM',
      'LOWES': 'LW',
      'Lowes': 'LW',
      'MENARDS': 'MD',
      'Menards': 'MD'
    };
    return abbreviations[channelName] || channelName.substring(0, 2).toUpperCase();
  };

  // Get full channel name (always returns full name, not affected by screen size)
  const getChannelFullName = (channelName: string): string => {
    const displayNames: {[key: string]: string} = {
      'HOME_DEPOT': 'Home Depot',
      'AMAZON': 'Amazon',
      'BESTBUY': 'BestBuy',
      'BESTBUY_CANADA': 'BestBuy CA',
      'BESTBUY_CA': 'BestBuy CA',
      'HOME_DEPOT_CANADA': 'Home Depot CA',
      'HOME_DEPOT_CA': 'Home Depot CA',
      'PROMRKT': 'PRO Market',
      'ProMrkt': 'PRO Market',
      'LOWES': 'Lowes',
      'MENARDS': 'Menards'
    };
    return displayNames[channelName] || channelName;
  };

  // Channel name display - responsive based on screen size
  const getChannelDisplayName = (channelName: string): string => {
    if (isMobile) {
      // Mobile: use abbreviations
      return getChannelAbbreviation(channelName);
    } else {
      // Desktop/Monitor: use full names
      return getChannelFullName(channelName);
    }
  };
  const [period, setPeriod] = useState('last_2_weeks');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuthStore();
  const { mode, isDesktop, isMonitor } = useViewMode();
  const { isFullscreen } = useFullscreen();

  // Set period to last 4 weeks when entering fullscreen (required since no time picker in fullscreen)
  useEffect(() => {
    if (isFullscreen && period !== 'last_4_weeks') {
      console.log('Entering fullscreen mode, changing period to last_4_weeks');
      setPeriod('last_4_weeks');
    }
  }, [isFullscreen, period]);

  // Handle window resize for chart responsiveness
  useEffect(() => {
    const handleResize = () => {
      // Force chart re-render on window resize
      window.dispatchEvent(new Event('resize'));
    };

    if (isFullscreen) {
      window.addEventListener('resize', handleResize);
      // Trigger initial resize after fullscreen mode is activated and data is loaded
      const timer = setTimeout(() => {
        if (dashboardData) {
          handleResize();
          // Force additional resize to fix width calculation issue in fullscreen
          setTimeout(() => {
            handleResize();
          }, 100);
        }
      }, 300); // Increased delay to ensure data is loaded
      
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(timer);
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isFullscreen, dashboardData]); // Added dashboardData as dependency
  const screens = useBreakpoint();

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params: any = { period };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      console.log('=== Fetching Dashboard Data ===');
      console.log('API params:', params);
      console.log('Is fullscreen:', isFullscreen);
      
      const response = await dashboardAPI.getOverview(params);
      
      console.log('=== API Response ===');
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Sales trend data:', response.data?.sales_trend);
      console.log('Channel distribution data:', response.data?.channel_distribution);
      
      if (response.data) {
        setDashboardData(response.data);
        
        // Validate data structure for fullscreen mode
        if (isFullscreen) {
          console.log('=== Fullscreen Data Validation ===');
          console.log('Sales trend datasets:', response.data.sales_trend?.datasets?.length || 0);
          console.log('Channel distribution datasets:', response.data.channel_distribution?.datasets?.length || 0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannelPerformanceData = async () => {
    setChannelPerformanceLoading(true);
    try {
      const params: any = { period };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      console.log('=== Fetching Channel Performance Data ===');
      console.log('Channel performance params:', params);
      console.log('Active tab:', activeTab);
      
      const response = await analyticsAPI.getChannelPerformance(params);
      const data = response.data;
      console.log('Channel performance response:', data);
      
      if (data && data.channels && data.datasets) {
        setChannelPerformanceData(data);
        
        // Initialize all channels as visible
        const initialVisibility: {[key: string]: boolean} = {};
        data.channels?.forEach((channel: string) => {
          initialVisibility[channel] = true;
        });
        setVisibleChannels(initialVisibility);
        
        if (isFullscreen) {
          console.log('=== Channel Performance Fullscreen Validation ===');
          console.log('Available channels:', data.channels?.length || 0);
          console.log('Datasets count:', data.datasets?.length || 0);
        }
      } else {
        console.warn('Channel performance data is incomplete:', data);
        // Set empty data instead of null to trigger proper UI state
        setChannelPerformanceData({ channels: [], datasets: [], labels: [] });
      }
    } catch (error) {
      console.error('Failed to fetch channel performance data:', error);
      // Set empty data on error to trigger proper error UI
      setChannelPerformanceData({ channels: [], datasets: [], labels: [] });
    } finally {
      setChannelPerformanceLoading(false);
    }
  };

  const fetchChannelRanking = async () => {
    setChannelRankingLoading(true);
    try {
      const response = await dashboardAPI.getChannelRanking();
      setChannelRankingData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching channel ranking data:', error);
    } finally {
      setChannelRankingLoading(false);
    }
  };

  const fetchSkuRanking = async () => {
    setSkuRankingLoading(true);
    try {
      const response = await dashboardAPI.getSkuRanking();
      setSkuRankingData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching SKU ranking data:', error);
    } finally {
      setSkuRankingLoading(false);
    }
  };

  useEffect(() => {
    console.log('Data fetch triggered - period:', period, 'isFullscreen:', isFullscreen);
    fetchDashboardData();
    fetchChannelPerformanceData();
    fetchChannelRanking();
    fetchSkuRanking();
  }, [period, dateRange, isFullscreen]); // Added isFullscreen as dependency

  // Fetch channel performance data when switching to channel performance tab
  useEffect(() => {
    if (activeTab === 'channel-performance') {
      console.log('Channel Performance tab activated - fetching data');
      fetchChannelPerformanceData();
    }
  }, [activeTab, period, dateRange, isFullscreen]); // Trigger when tab becomes active or data dependencies change

  // Reset selected channel and refetch data when switching to channel performance tab
  useEffect(() => {
    if (activeTab === 'channel-performance' && !channelPerformanceData) {
      console.log('Channel Performance tab needs data - resetting and fetching');
      setSelectedChannel('All');
      fetchChannelPerformanceData();
    }
  }, [activeTab, channelPerformanceData]);

  // Force re-render when switching to channel performance tab in fullscreen mode
  useEffect(() => {
    if (isFullscreen && activeTab === 'channel-performance') {
      console.log('Fullscreen channel performance tab activated - forcing layout update');
      // Force a small delay to ensure DOM is ready, then trigger resize
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        // Force additional layout recalculation
        const tabContent = document.querySelector('.ant-tabs-content') as HTMLElement;
        if (tabContent) {
          tabContent.style.display = 'none';
          void tabContent.offsetHeight; // Trigger reflow - explicitly void to satisfy ESLint
          tabContent.style.display = '';
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isFullscreen, activeTab]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchDashboardData();
      fetchChannelPerformanceData();
    };
    
    window.addEventListener('dashboard-refresh', handleRefresh);
    return () => {
      window.removeEventListener('dashboard-refresh', handleRefresh);
    };
  }, [period, dateRange]);

  // Adjust chart height based on view mode
  const getChartHeight = (chartType: 'main' | 'side' | 'full') => {
    if (isFullscreen) {
      // Fullscreen mode - optimized for maximum chart visibility
      const availableHeight = window.innerHeight;
      const kpiHeight = 50; // Compact KPI cards
      const padding = 8; // Minimal padding
      const gaps = 8; // Minimal gaps
      const tabsHeight = 40;
      const buttonAreaHeight = 60; // Space for channel selection buttons
      const tableAreaHeight = 280; // Reserve space for bottom tables (320px + margins)
      const remainingHeight = availableHeight - kpiHeight - padding - gaps - tabsHeight - buttonAreaHeight - tableAreaHeight;
      
      // Allocate space to charts in fullscreen, reduced to make room for tables
      return chartType === 'main' ? `${Math.floor(remainingHeight * 0.7)}px` : // Sales trend
             chartType === 'side' ? `${Math.floor(remainingHeight * 0.7)}px` : // Channel distribution (reduced)
             `${Math.floor(remainingHeight * 0.5)}px`; // Channel performance (reduced)
    } else if (isMonitor) {
      // Monitor mode - normal layout
      const headerHeight = 60;
      const availableHeight = window.innerHeight - headerHeight;
      const kpiHeight = 100;
      const padding = 40;
      const gaps = 36;
      const remainingHeight = availableHeight - kpiHeight - padding - gaps;
      
      return chartType === 'main' ? `${Math.floor(remainingHeight * 0.6 - 32)}px` : 
             chartType === 'side' ? `${Math.floor(remainingHeight * 0.6 - 32)}px` : 
             `${Math.floor(remainingHeight * 0.4 - 32)}px`;
    }
    if (isMobile) {
      return chartType === 'main' ? '250px' : chartType === 'side' ? '220px' : '280px';
    }
    return chartType === 'main' ? '360px' : chartType === 'side' ? '360px' : '500px';
  };

  // Sales trend dual-axis chart configuration - according to README.md requirements
  const getSalesTrendOption = () => {
    console.log('=== Sales Trend Data Debug ===');
    console.log('dashboardData:', dashboardData);
    console.log('sales_trend:', dashboardData?.sales_trend);
    console.log('isFullscreen:', isFullscreen);
    
    if (!dashboardData?.sales_trend) {
      console.log('No sales_trend data available');
      return {
        title: {
          text: 'Sales Trend Data Loading...',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: isFullscreen ? 'Please wait while loading last 4 weeks data...' : 'No data available for the selected period',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    // Check if datasets exist and have data
    if (!dashboardData.sales_trend.datasets || dashboardData.sales_trend.datasets.length === 0) {
      console.log('No datasets in sales_trend');
      return {
        title: {
          text: 'No Sales Data Available',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: 'No sales data found for the selected period',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    // Validate that datasets have actual data
    const hasValidData = dashboardData.sales_trend.datasets.some(dataset => 
      dataset.data && dataset.data.length > 0 && dataset.data.some((value: number) => value > 0)
    );
    
    if (!hasValidData) {
      console.log('Datasets exist but contain no valid data');
      console.log('Dataset details:', dashboardData.sales_trend.datasets);
      return {
        title: {
          text: 'No Valid Sales Data',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: 'Sales data exists but contains no valid values',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    const fontSize = isMonitor ? 16 : isMobile ? 10 : 14;
    const titleSize = isMonitor ? 20 : isMobile ? 14 : 18;
    
    // Use real data returned by API with safe fallbacks
    const labels = dashboardData.sales_trend.labels || [];
    const sellThroughSalesActual = dashboardData.sales_trend.datasets.find(d => d.label && d.label.includes('Sale-through Sales (Actual)'))?.data || [];
    const sellThroughSalesTarget = dashboardData.sales_trend.datasets.find(d => d.label && d.label.includes('Sale-through Sales (Target)'))?.data || [];
    const sellThroughUnits = dashboardData.sales_trend.datasets.find(d => d.label && d.label.includes('Sale-through Units'))?.data || [];
    const posSales = dashboardData.sales_trend.datasets.find(d => d.label && d.label.includes('POS'))?.data || [];
    const promotions = dashboardData.sales_trend.promotions || [];
    
    // Ensure all arrays have the same length as labels
    const dataLength = labels.length;
    const safeActualData = Array.from({length: dataLength}, (_, i) => sellThroughSalesActual[i] || 0);
    const safeTargetData = Array.from({length: dataLength}, (_, i) => sellThroughSalesTarget[i] || 0);
    const safeUnitsData = Array.from({length: dataLength}, (_, i) => sellThroughUnits[i] || 0);
    const safePosData = Array.from({length: dataLength}, (_, i) => posSales[i] || 0);
    
    // Prepare promotion data for bar charts with safe data handling
    const amazonPromotionData = labels.map(label => {
      const promo = promotions.find((p: PromotionData) => p.week_date === label);
      return promo ? (promo.amazon_promotion || 0) : 0;
    });
    const shopifyPromotionData = labels.map(label => {
      const promo = promotions.find((p: PromotionData) => p.week_date === label);
      return promo ? (promo.shopify_promotion || 0) : 0;
    });
    
    console.log('=== Frontend Sales Trend Debug ===');
    console.log('Available datasets:', dashboardData.sales_trend.datasets.map(d => d.label));
    console.log('Labels:', labels);
    console.log('Sell-through Sales (Actual) data:', safeActualData);
    console.log('Sell-through Sales (Target) data:', safeTargetData);
    console.log('Sell-through Units data:', safeUnitsData);
    console.log('POS Sales data:', safePosData);
    console.log('Promotions data:', promotions);
    
    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontSize },
        formatter: (params: any[]) => {
          let result = `${params[0].name}<br/>`;
          params.forEach((param: any) => {
            if (param.seriesName.includes('Units')) {
              result += `${param.seriesName}: ${param.value.toLocaleString()} Units<br/>`;
            } else if (param.seriesName.includes('Promotions')) {
              result += `${param.seriesName}: $${param.value.toLocaleString()}<br/>`;
            } else {
              result += `${param.seriesName}: $${param.value.toLocaleString()}<br/>`;
            }
          });
          return result;
        },
      },
      legend: {
        data: ['ST Sales (Actual)', 'ST Sales (Target)', 'ST Units', 'POS Sales', 'Amazon Promotions', 'Shopify Promotions'],
        textStyle: { 
          fontSize,
          color: isFullscreen ? '#FFFFFF' : '#000000' // High contrast white for fullscreen
        },
        top: 0,
        itemGap: 15,
        orient: 'horizontal',
        left: 'center',
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: {
          lineStyle: { color: isFullscreen ? '#666666' : '#E5E5E5' },
        },
        axisLabel: {
          color: isFullscreen ? '#E0E0E0' : '#86909C', // High contrast light gray for fullscreen
          fontSize,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Sales ($)',
          position: 'left',
          nameTextStyle: {
            color: '#1890ff',
            fontSize: fontSize + 2,
            fontWeight: 'bold',
          },
          axisLine: {
            lineStyle: { color: '#E5E5E5' },
          },
          axisLabel: {
            color: isFullscreen ? '#E0E0E0' : '#86909C', // High contrast light gray for fullscreen
            fontSize,
            formatter: (value: number) => `$${(value / 1000).toFixed(0)}K`,
          },
          splitLine: {
            lineStyle: { color: '#F5F5F5' },
          },
        },
        {
          type: 'value',
          name: 'Units',
          position: 'right',
          show: true,
          nameTextStyle: {
            color: '#fa8c16',
            fontSize: fontSize + 2,
            fontWeight: 'bold',
          },
          axisLine: {
            show: true,
            lineStyle: { color: '#fa8c16' },
          },
          axisLabel: {
            show: true,
            color: isFullscreen ? '#E0E0E0' : '#86909C', // High contrast light gray for fullscreen
            fontSize,
            formatter: (value: number) => `${value.toLocaleString()}`,
          },
          splitLine: {
            show: false,
          },
        },
        {
          type: 'value',
          name: 'Promo ($)',
          position: 'left',
          offset: 80,
          nameTextStyle: {
            color: '#ff4d4f',
            fontSize: fontSize + 2,
            fontWeight: 'bold',
          },
          axisLine: {
            show: true,
            lineStyle: { color: '#ff4d4f' },
          },
          axisLabel: {
            show: true,
            color: isFullscreen ? '#E0E0E0' : '#86909C', // High contrast light gray for fullscreen
            fontSize,
            formatter: (value: number) => `$${(value / 1000).toFixed(0)}K`,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: 'ST Sales (Actual)',
          data: safeActualData,
          type: 'line',
          yAxisIndex: 0,
          smooth: true,
          lineStyle: {
            color: isFullscreen ? '#00D4FF' : '#1890ff', // High contrast cyan for fullscreen
            width: isMonitor ? 4 : 3,
          },
          symbol: 'circle',
          symbolSize: isMonitor ? 8 : 6,
          itemStyle: {
            color: isFullscreen ? '#00D4FF' : '#1890ff',
          },
        },
        {
          name: 'ST Sales (Target)',
          data: safeTargetData,
          type: 'line',
          yAxisIndex: 0,
          smooth: true,
          lineStyle: {
            color: isFullscreen ? '#FF1744' : '#ff4d4f', // High contrast red for fullscreen
            width: isMonitor ? 4 : 3,
            type: 'dashed',
          },
          symbol: 'diamond',
          symbolSize: isMonitor ? 8 : 6,
          itemStyle: {
            color: isFullscreen ? '#FF1744' : '#ff4d4f',
          },
        },
        {
          name: 'ST Units',
          data: safeUnitsData,
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          lineStyle: {
            color: isFullscreen ? '#FFD700' : '#fa8c16', // High contrast gold for fullscreen
            width: isMonitor ? 3 : 2,
          },
          symbol: 'triangle',
          symbolSize: isMonitor ? 6 : 4,
          itemStyle: {
            color: isFullscreen ? '#FFD700' : '#fa8c16',
          },
        },
        {
          name: 'POS Sales',
          data: safePosData,
          type: 'line',
          yAxisIndex: 0,
          smooth: true,
          lineStyle: {
            color: isFullscreen ? '#9C27B0' : '#722ed1', // High contrast purple for fullscreen
            width: isMonitor ? 2 : 1,
            type: 'dotted',
          },
          symbol: 'rect',
          symbolSize: isMonitor ? 4 : 3,
          itemStyle: {
            color: isFullscreen ? '#9C27B0' : '#722ed1',
          },
        },
        {
          name: 'Amazon Promotions',
          data: amazonPromotionData,
          type: 'bar',
          yAxisIndex: 2,
          stack: 'promotions',
          itemStyle: {
            color: getChannelColor('Amazon') + '99', // Add transparency
          },
          barWidth: '60%',
        },
        {
          name: 'Shopify Promotions',
          data: shopifyPromotionData,
          type: 'bar',
          yAxisIndex: 2,
          stack: 'promotions',
          itemStyle: {
            color: getChannelColor('Shopify') + '99', // Add transparency
          },
          barWidth: '60%',
        },
      ],
      grid: {
        left: '8%',
        right: '6%',
        bottom: '10%',
        top: '15%',
        containLabel: true,
      },

    };
  };

  // Channel performance matrix configuration - bubble chart
  const getChannelDistributionOption = () => {
    console.log('=== Channel Distribution Data Debug ===');
    console.log('dashboardData:', dashboardData);
    console.log('channel_distribution:', dashboardData?.channel_distribution);
    console.log('isFullscreen:', isFullscreen);
    
    if (!dashboardData?.channel_distribution) {
      console.log('No channel_distribution data available');
      return {
        title: {
          text: 'Channel Performance Data Loading...',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: isFullscreen ? 'Please wait while loading channel data...' : 'No channel data available for the selected period',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    // Check if datasets exist and have data
    if (!dashboardData.channel_distribution.datasets || dashboardData.channel_distribution.datasets.length === 0) {
      console.log('No datasets in channel_distribution');
      return {
        title: {
          text: 'No Channel Performance Data',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: 'No channel performance data found for the selected period',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    // Validate that datasets have actual data
    const hasValidChannelData = dashboardData.channel_distribution.datasets.some(dataset => 
      dataset.data && dataset.data.length > 0 && 
      dataset.data.some((item: any) => (item.sales || item.sell_through_sales || 0) > 0)
    );
    
    if (!hasValidChannelData) {
      console.log('Channel datasets exist but contain no valid data');
      console.log('Channel dataset details:', dashboardData.channel_distribution.datasets);
      return {
        title: {
          text: 'No Valid Channel Data',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: 'Channel data exists but contains no valid sales values',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    const fontSize = isMonitor ? 16 : isMobile ? 10 : 14;
    const titleSize = isMonitor ? 20 : isMobile ? 14 : 18;
    
    // Use real channel data returned by API with safe data handling
    const channelData = dashboardData.channel_distribution.datasets?.[0]?.data || [];
    
    // Extract channel types and color configuration from API data
    const channelTypes = dashboardData.channel_distribution.labels || [];
    
    // Filter out channels with no valid data
    const validChannelData = channelData.filter((item: any) => {
      const sales = item.sales || item.sell_through_sales || 0;
      const units = item.units || item.sell_through_units || 0;
      return sales > 0 || units > 0;
    });
    
    console.log('=== Channel Data Validation ===');
    console.log('Total channel data items:', channelData.length);
    console.log('Valid channel data items:', validChannelData.length);
    console.log('Channel data details:', channelData);
    
    return {
      title: {
        text: 'Channel Performance Matrix',
        textStyle: {
          fontSize: titleSize,
          fontWeight: 600,
          color: isFullscreen ? '#FFFFFF' : '#1D1D1F', // High contrast white for fullscreen
        },
        top: isFullscreen ? 'auto' : 'auto', // Move title up in fullscreen mode
      },
      tooltip: {
        trigger: 'item',
        textStyle: { fontSize },
        formatter: (params: any) => {
          const data = params.data;
          const channelAbbr = data.name; // This is the abbreviation shown on the chart
          const channelFull = getChannelFullName(data.originalName || data.name); // Get full name
          const targetAchievement = Number(data.value[1]).toFixed(2); // Format to 2 decimal places
          return `<strong>Channel: ${channelAbbr} (${channelFull})</strong><br/>
                  Sales Amount: $${data.value[0].toLocaleString()}<br/>
                  Target Achievement: ${targetAchievement}%<br/>
                  Sales Volume: ${data.value[2].toLocaleString()} Units`;
        },
      },
      legend: {
        data: channelTypes,
        textStyle: { 
          fontSize,
          color: isFullscreen ? '#FFFFFF' : '#000000' // High contrast white for fullscreen
        },
        top: isFullscreen ? '8%' : 30, // Adjust legend position for fullscreen
        itemGap: 20,
      },
      xAxis: {
        type: 'value',
        name: 'Sale-through Sales ($)',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          color: isFullscreen ? '#FFFFFF' : '#1D1D1F', // High contrast white for fullscreen
          fontSize: fontSize + 2,
          fontWeight: 600,
        },
        axisLine: {
          lineStyle: { color: isFullscreen ? '#666666' : '#E5E5E5' },
        },
        axisLabel: {
          color: isFullscreen ? '#E0E0E0' : '#1D1D1F', // High contrast light gray for fullscreen
          fontSize,
          fontWeight: 500,
          formatter: (value: number) => `$${(value / 1000).toFixed(0)}K`,
        },
        splitLine: {
          lineStyle: { color: '#F5F5F5' },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Target Achievement (%)',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: isFullscreen ? '#FFFFFF' : '#1D1D1F', // High contrast white for fullscreen
          fontSize: fontSize + 2,
          fontWeight: 600,
        },
        axisLine: {
          lineStyle: { color: isFullscreen ? '#666666' : '#E5E5E5' },
        },
        axisLabel: {
          color: isFullscreen ? '#E0E0E0' : '#1D1D1F', // High contrast light gray for fullscreen
          fontSize,
          fontWeight: 500,
          formatter: (value: number) => `${value}%`,
        },
        splitLine: {
          lineStyle: { color: '#F5F5F5' },
        },
        // Add 100% baseline
        markLine: {
          data: [
            {
              yAxis: 100,
              lineStyle: {
                color: '#ff4d4f',
                type: 'dashed',
                width: 2,
              },
              label: {
                formatter: 'Target Line (100%)',
                position: 'end',
              },
            },
          ],
        },
      },
      series: dashboardData.channel_distribution.datasets.map((dataset: any, index: number) => ({
        name: dataset.label,
        data: validChannelData.map((item: any) => ({
          name: getChannelAbbreviation(item.name || item.channel_name),
          originalName: item.name || item.channel_name, // Store original name for tooltip
          value: [
            item.sales || item.sell_through_sales || 0, 
            item.achievement || item.achievement_rate || 95, 
            item.units || item.sell_through_units || 0
          ],
          symbolSize: Math.max(10, Math.min(50, Math.sqrt((item.units || item.sell_through_units || 100)) / 5)),
          itemStyle: {
            color: getChannelColor(item.name || item.channel_name),
            opacity: 0.8,
            shadowBlur: 10,
            shadowColor: 'rgba(120, 36, 50, 0.5)',
            shadowOffsetY: 5,
          },
        })),
        type: 'scatter',
        symbolSize: (data: any) => {
          return Math.max(10, Math.min(50, Math.sqrt(data[2] || 100) / 5));
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 20,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => params.data.name,
          textStyle: {
            color: isFullscreen ? '#FFFFFF' : '#1D1D1F', // High contrast white for fullscreen
            fontSize: fontSize - 2,
            fontWeight: 500,
          },
          emphasis: {
            show: true,
            textStyle: {
              color: isFullscreen ? '#FFFFFF' : '#1D1D1F',
              fontSize: fontSize,
              fontWeight: 600,
            },
          },
        },
      })),
      grid: {
        left: '10%',
        right: '5%',
        bottom: '15%',
        top: isFullscreen ? '15%' : '20%', // Reduce top margin in fullscreen to accommodate title position
        containLabel: true,
      },
    };
  };

  // Channel colors mapping - optimized for better contrast and visual distinction
  const getChannelColor = (channel: string) => {
    console.log('Channel name for color mapping:', channel); // Debug log
    
    // High contrast colors for fullscreen mode
    const fullscreenColorMap: {[key: string]: string} = {
      'Amazon': '#FF6B00',        // High saturation orange
      'AMAZON': '#FF6B00',        
      'Home Depot': '#FF4500',    // Bright orange red
      'HOME_DEPOT': '#FF4500',    
      'Lowes': '#0080FF',         // Bright blue
      'LOWES': '#0080FF',         
      'Bestbuy': '#FFD700',       // Gold yellow
      'BESTBUY': '#FFD700',       
      'Menards': '#FF1744',       // Bright red
      'MENARDS': '#FF1744',       
      'Shopify': '#00E676',       // Bright green
      'SHOPIFY': '#00E676',       
      'Home Depot Canada': '#FF5722', // Deep orange
      'HOME_DEPOT_CA': '#FF5722', 
      'BestBuy Canada': '#9C27B0', // Bright purple
      'BESTBUY_CA': '#9C27B0',    
      'ProMrkt': '#00BCD4',       // Cyan
      'PROMRKT': '#00BCD4'        
    };
    
    // Standard colors for normal mode
    const standardColorMap: {[key: string]: string} = {
      'Amazon': '#FF9500',        
      'AMAZON': '#FF9500',        
      'Home Depot': '#F96302',    
      'HOME_DEPOT': '#F96302',    
      'Lowes': '#004CCC',         
      'LOWES': '#004CCC',         
      'Bestbuy': '#FFE000',       
      'BESTBUY': '#FFE000',       
      'Menards': '#DC2626',       
      'MENARDS': '#DC2626',       
      'Shopify': '#95BF47',       
      'SHOPIFY': '#95BF47',       
      'Home Depot Canada': '#EA580C', 
      'HOME_DEPOT_CA': '#EA580C', 
      'BestBuy Canada': '#7C3AED', 
      'BESTBUY_CA': '#7C3AED',    
      'ProMrkt': '#059669',       
      'PROMRKT': '#059669'        
    };
    
    const colorMap = isFullscreen ? fullscreenColorMap : standardColorMap;
    return colorMap[channel] || (isFullscreen ? '#00D4FF' : '#1890FF'); // High contrast default for fullscreen
  };

  // Channel performance chart configuration
  const getChannelPerformanceOption = () => {
    console.log('=== Channel Performance Chart Debug ===');
    console.log('channelPerformanceData:', channelPerformanceData);
    console.log('selectedChannel:', selectedChannel);
    console.log('activeTab:', activeTab);
    console.log('isFullscreen:', isFullscreen);
    
    if (!channelPerformanceData || !channelPerformanceData.datasets) {
      return {
        title: {
          text: 'Channel Performance Loading...',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: 'Loading channel performance data...',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    // Validate that datasets have valid channel data
    if (!channelPerformanceData.datasets || channelPerformanceData.datasets.length === 0) {
      console.log('No datasets found in channel performance data');
      return {
        title: {
          text: 'No Channel Data Available',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: isMonitor ? 18 : 16,
            color: isFullscreen ? '#FFFFFF' : '#666666'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: '60%',
          style: {
            text: 'No channel performance data found for the selected period',
            fontSize: isMonitor ? 16 : 14,
            fill: isFullscreen ? '#CCCCCC' : '#999999'
          }
        }
      };
    }
    
    const fontSize = isMonitor ? 16 : isMobile ? 10 : 14;
    const titleSize = isMonitor ? 20 : isMobile ? 14 : 18;
    
    let series: any[] = [];
    let legendData: string[] = [];
    let title = 'Channel Sales Trends';
    
    if (selectedChannel === 'All') {
      // Show all channels as stacked bar chart
      const channelSeries = channelPerformanceData.datasets.map((dataset: any) => ({
        name: getChannelDisplayName(dataset.channel),
        type: 'bar',
        stack: 'actual',
        data: dataset.sales_data,
        itemStyle: {
          color: getChannelColor(dataset.channel),
        },
      }));
      
      // Calculate total target data (sum of all channels' target_data)
      const totalTargetData = channelPerformanceData.labels.map((_: any, index: number) => {
        const totalTarget = channelPerformanceData.datasets.reduce((sum: number, dataset: any) => {
          return sum + (dataset.target_data?.[index] || 0);
        }, 0);
        return totalTarget;
      });
      
      // Add target line
      const targetSeries = {
        name: 'Sales (Target)',
        type: 'line',
        data: totalTargetData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 3,
          color: isFullscreen ? '#FF1744' : '#ff4d4f', // High contrast red for fullscreen
          type: 'solid',
        },
        itemStyle: {
          color: isFullscreen ? '#FF1744' : '#ff4d4f', // High contrast red for fullscreen
        },
      };
      
      series = [...channelSeries, targetSeries];
      legendData = [...channelPerformanceData.datasets.map((d: any) => getChannelDisplayName(d.channel)), 'Sales (Target)'];
      title = 'Channel Sales Trends - All Channels';
    } else {
      // Show single channel as stacked bar chart
      const selectedChannelData = channelPerformanceData.datasets.find(
        (dataset: any) => dataset.channel === selectedChannel
      );
      
      if (!selectedChannelData) return {};
      
      // Single channel actual sales as bar
      const actualSeries = {
        name: getChannelDisplayName(selectedChannel),
        type: 'bar',
        data: selectedChannelData.sales_data,
        itemStyle: {
          color: getChannelColor(selectedChannel),
        },
      };
      
      // Single channel target as line
      const targetSeries = {
        name: 'Sales (Target)',
        type: 'line',
        data: selectedChannelData.target_data || [],
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 3,
          color: isFullscreen ? '#FF1744' : '#ff4d4f', // High contrast red for fullscreen
          type: 'solid',
        },
        itemStyle: {
          color: isFullscreen ? '#FF1744' : '#ff4d4f', // High contrast red for fullscreen
        },
      };
      
      series = [actualSeries, targetSeries];
      legendData = [getChannelDisplayName(selectedChannel), 'Sales (Target)'];
      title = `Channel Sales Trends - ${getChannelDisplayName(selectedChannel)}`;
    }
    
    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontSize },
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        formatter: (params: any) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach((param: any) => {
            result += `${param.marker}${param.seriesName}: $${param.value.toLocaleString()}<br/>`;
          });
          return result;
        },
      },
      legend: {
        data: legendData,
        textStyle: { 
          fontSize,
          color: isFullscreen ? '#FFFFFF' : '#000000' // High contrast white for fullscreen
        },
        top: 20,
        selectedMode: false,
      },
      xAxis: {
        type: 'category',
        data: channelPerformanceData.labels || [],
        axisLabel: {
          color: isFullscreen ? '#E0E0E0' : '#86909C', // High contrast light gray for fullscreen
          fontSize,
          rotate: 45,
        },
        axisLine: {
          lineStyle: { color: isFullscreen ? '#666666' : '#E5E5E5' },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: isFullscreen ? '#E0E0E0' : '#86909C', // High contrast light gray for fullscreen
          fontSize,
          formatter: (value: number) => `$${(value / 1000).toFixed(0)}K`,
        },
        axisLine: {
          lineStyle: { color: isFullscreen ? '#666666' : '#E5E5E5' },
        },
        splitLine: {
          lineStyle: { color: '#F5F5F5' },
        },
      },
      series: series,
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '20%',
        containLabel: true,
      },
    };
  };



  const renderKPICard = (kpi: KPICardData, index: number) => {
    const getKPIIcon = (title: string) => {
      if (title.includes('Sale-in') && title.includes('Sales')) return <DollarOutlined />;
      if (title.includes('Sale-through') && title.includes('Sales')) return <DollarOutlined />;
      if (title.includes('Sale-in') && title.includes('Units')) return <SafetyOutlined />;
      if (title.includes('Sale-through') && title.includes('Units')) return <SafetyOutlined />;
      if (title.includes('POS')) return <DollarOutlined />;
      if (title.includes('Installation')) return <SafetyOutlined />;
      if (title.includes('Average')) return <SafetyOutlined />;
      if (title.includes('Rate')) return <TrophyOutlined />;
      return <TrophyOutlined />;
    };

    return (
      <Col 
        xs={24} 
        sm={12} 
        md={8}
        lg={6}
        xl={3}
        key={index}
      >
        <Card 
          className={`kpi-card ${mode}-kpi-card`} 
          style={{
            height: isMonitor ? '140px' : '160px',
            marginBottom: '8px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}
          bodyStyle={{ 
            padding: isMonitor ? '8px 12px 12px 12px' : '12px 16px 16px 16px',
            height: '100%'
          }}
        >
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            position: 'relative'
          }}>
            {/* Icon and title area */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              marginBottom: isMonitor ? '6px' : '10px',
              minHeight: isMonitor ? '20px' : '24px'
            }}>
              <div style={{
                fontSize: isMonitor ? '16px' : '18px',
                color: '#1890ff',
                marginRight: '8px',
                lineHeight: '1'
              }}>
                {getKPIIcon(kpi.title)}
              </div>
              <div style={{
                fontSize: isMonitor ? '12px' : '13px',
                color: isFullscreen ? '#d9d9d9' : '#666',
                fontWeight: '500',
                lineHeight: '1.4',
                letterSpacing: '0.2px'
              }}>
                {kpi.title}
              </div>
            </div>
            
            {/* Main value area */}
            <div style={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginBottom: isMonitor ? '4px' : '6px'
            }}>
              <div style={{ 
                fontSize: isMonitor ? '24px' : '28px', 
                fontWeight: '600', 
                color: isFullscreen ? '#ffffff' : '#262626',
                lineHeight: '1.2',
                letterSpacing: '-0.5px'
              }}>
                {kpi.value}
              </div>
            </div>
            
            {/* Bottom information area */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              {/* Target comparison row */}
              {kpi.target && (
                <div style={{ 
                  fontSize: isMonitor ? '11px' : '12px',
                  color: isFullscreen ? '#e8e8e8' : '#8c8c8c',
                  lineHeight: '1.3',
                  fontWeight: '400'
                }}>
                  {kpi.target}
                </div>
              )}
              
              {/* Achievement status row */}
              {kpi.achievement_text && (
                <div style={{ 
                  fontSize: isMonitor ? '11px' : '12px',
                  fontWeight: '500',
                  lineHeight: '1.3',
                  color: isFullscreen ? 
                    (kpi.achievement_text.includes('Growth') || kpi.achievement_text.includes('Excellent') ? '#73d13d' :
                     kpi.achievement_text.includes('Decline') ? '#ff7875' : '#40a9ff') :
                    (kpi.achievement_text.includes('Growth') || kpi.achievement_text.includes('Excellent') ? '#52c41a' :
                     kpi.achievement_text.includes('Decline') ? '#ff4d4f' : '#1890ff')
                }}>
                  {kpi.achievement_text}
                </div>
              )}
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  // Alert warning component with scrolling animation
  const renderAlerts = () => {
    if (!dashboardData?.alerts || dashboardData.alerts.length === 0) return null;

    const getAlertIcon = (severity: string) => {
      switch (severity) {
        case 'high': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
        case 'medium': return <WarningOutlined style={{ color: '#fa8c16' }} />;
        case 'low': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        default: return <WarningOutlined />;
      }
    };

    const getSeverityColor = (severity: string) => {
      switch (severity) {
        case 'high': return '#ff4d4f';
        case 'medium': return '#fa8c16';
        case 'low': return '#52c41a';
        default: return '#fa8c16';
      }
    };

    return (
       <div className="alert-system-container" style={{ marginBottom: 10 }}>
         <div className="alert-scrolling-wrapper">
           <div className="alert-scrolling-content">
             {dashboardData.alerts.map((alert, index) => (
               <div key={index} className={`alert-scroll-item severity-${alert.severity}`}>
                 <Space size={8}>
                   {getAlertIcon(alert.severity)}
                   <span className="alert-title">{alert.title}</span>
                   <span className="alert-message">- {alert.message}</span>
                   <Tag 
                     color={alert.severity === 'high' ? 'red' : alert.severity === 'medium' ? 'orange' : 'green'}
                     style={{ marginLeft: 8 }}
                   >
                     {alert.severity === 'high' ? 'High' : alert.severity === 'medium' ? 'Medium' : 'Low'}
                   </Tag>
                 </Space>
               </div>
             ))}
             {/* Duplicate alerts for seamless scrolling */}
             {dashboardData.alerts.map((alert, index) => (
               <div key={`duplicate-${index}`} className={`alert-scroll-item severity-${alert.severity}`}>
                 <Space size={8}>
                   {getAlertIcon(alert.severity)}
                   <span className="alert-title">{alert.title}</span>
                   <span className="alert-message">- {alert.message}</span>
                   <Tag 
                     color={alert.severity === 'high' ? 'red' : alert.severity === 'medium' ? 'orange' : 'green'}
                     style={{ marginLeft: 8 }}
                   >
                     {alert.severity === 'high' ? 'High' : alert.severity === 'medium' ? 'Medium' : 'Low'}
                   </Tag>
                 </Space>
               </div>
             ))}
           </div>
         </div>
       </div>
     );
  };

  // Product table column definition
  const productColumns = [
    {
      title: 'SKU Code',
      dataIndex: 'sku_code',
      key: 'sku_code',
      width: 120,
    },
    {
      title: 'SKU Name',
      dataIndex: 'sku_name',
      key: 'sku_name',
      ellipsis: true,
    },
    {
      title: 'Sell-in Sales',
      dataIndex: 'sell_in_sales',
      key: 'sell_in_sales',
      render: (value: number) => `$${value.toLocaleString()}`,
      sorter: (a: ProductTableData, b: ProductTableData) => a.sell_in_sales - b.sell_in_sales,
    },
    {
      title: 'Sell-through Sales',
      dataIndex: 'sell_through_sales',
      key: 'sell_through_sales',
      render: (value: number) => `$${value.toLocaleString()}`,
      sorter: (a: ProductTableData, b: ProductTableData) => a.sell_through_sales - b.sell_through_sales,
    },
    {
      title: 'POS Sales',
      dataIndex: 'pos_sales',
      key: 'pos_sales',
      render: (value: number) => `$${value.toLocaleString()}`,
      sorter: (a: ProductTableData, b: ProductTableData) => a.pos_sales - b.pos_sales,
    },
    {
      title: 'Installation',
      dataIndex: 'installation_count',
      key: 'installation_count',
      render: (value: number) => value.toLocaleString(),
      sorter: (a: ProductTableData, b: ProductTableData) => a.installation_count - b.installation_count,
    },
    {
      title: 'Sell-through Rate',
      dataIndex: 'sell_through_rate',
      key: 'sell_through_rate',
      render: (value: number) => (
        <Space>
          <Progress 
            percent={Math.min(value, 100)} 
            size="small" 
            status={value < 50 ? 'exception' : value > 80 ? 'success' : 'normal'}
            showInfo={false}
            style={{ width: 60 }}
          />
          <span>{value.toFixed(1)}%</span>
        </Space>
      ),
      sorter: (a: ProductTableData, b: ProductTableData) => a.sell_through_rate - b.sell_through_rate,
    },
  ];

  // Sales data table column definition
  const salesColumns = [
    {
      title: 'Period',
      dataIndex: 'week_date',
      key: 'week_date',
      width: 100,
    },
    {
      title: 'Channel',
      dataIndex: 'channel_name',
      key: 'channel_name',
      width: 120,
    },
    {
      title: 'Sell-in Sales',
      dataIndex: 'sell_in_sales',
      key: 'sell_in_sales',
      render: (value: number) => `$${value.toLocaleString()}`,
      sorter: (a: SalesTableData, b: SalesTableData) => a.sell_in_sales - b.sell_in_sales,
    },
    {
      title: 'Sell-through Sales',
      dataIndex: 'sell_through_sales',
      key: 'sell_through_sales',
      render: (value: number) => `$${value.toLocaleString()}`,
      sorter: (a: SalesTableData, b: SalesTableData) => a.sell_through_sales - b.sell_through_sales,
    },
    {
      title: 'POS Sales',
      dataIndex: 'pos_sales',
      key: 'pos_sales',
      render: (value: number) => `$${value.toLocaleString()}`,
      sorter: (a: SalesTableData, b: SalesTableData) => a.pos_sales - b.pos_sales,
    },
    {
      title: 'Installation',
      dataIndex: 'installation_count',
      key: 'installation_count',
      render: (value: number) => value.toLocaleString(),
      sorter: (a: SalesTableData, b: SalesTableData) => a.installation_count - b.installation_count,
    },
    {
      title: 'Sell-through Rate',
      dataIndex: 'sell_through_rate',
      key: 'sell_through_rate',
      render: (value: number) => (
        <Space>
          <Progress 
            percent={Math.min(value, 100)} 
            size="small" 
            status={value < 50 ? 'exception' : value > 80 ? 'success' : 'normal'}
            showInfo={false}
            style={{ width: 60 }}
          />
          <span>{value.toFixed(1)}%</span>
        </Space>
      ),
      sorter: (a: SalesTableData, b: SalesTableData) => a.sell_through_rate - b.sell_through_rate,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Page Title and Controls - Hidden in fullscreen */}
      {!isFullscreen && (
        <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              Sales Dashboard
              <span style={{ fontSize: 14, color: '#86909C', marginLeft: 16 }}>
                Welcome back, {user?.full_name || user?.username}
              </span>
            </Title>
          </Col>
          <Col>
            <Space>
              <Select
                value={period}
                onChange={setPeriod}
                style={{ width: 150 }}
                options={[
                  { label: 'This Week', value: 'week' },
                  { label: 'Last Week', value: 'last_week' },
                  { label: 'Last 2 Weeks', value: 'last_2_weeks' },
                  { label: 'Last 4 Weeks', value: 'last_4_weeks' },
                  { label: 'Last 12 Weeks', value: 'last_12_weeks' },
                  { label: 'This Year', value: 'year' },
                ]}
              />
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                allowClear
              />
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => {
                  fetchDashboardData();
                  fetchChannelPerformanceData();
                }}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      )}

      {/* Alert Area */}
      {renderAlerts()}
      


      {/* KPI Cards - 8 columns in one row */}
      {dashboardData?.kpi_cards && (
        <Row gutter={[4, 8]} style={{ marginBottom: 0 }}>
          {(() => {
            // Reorder KPI cards according to specified order
            const orderedTitles = [
              'Sale-in Sales',
              'Sale-in Units', 
              'Sale-through Sales',
              'Sale-through Units',
              'Sale-through Rate',
              'POS Sales',
              'Installation',
              'Average Daily'
            ];
            
            return orderedTitles.map((title, index) => {
              const kpi = dashboardData.kpi_cards.find(k => k.title === title);
              return kpi ? renderKPICard(kpi, index) : null;
            }).filter(Boolean);
          })()}
        </Row>
      )}

      {/* Tab Content */}
      <Tabs 
        activeKey={activeTab} 
        onChange={(key) => {
          console.log('Tab switching from', activeTab, 'to', key, 'in fullscreen:', isFullscreen);
          setActiveTab(key);
          
          // Force refresh channel data when switching to channel-performance in fullscreen
          if (isFullscreen && key === 'channel-performance') {
            console.log('Forcing channel performance data refresh in fullscreen mode');
            setTimeout(() => {
              fetchChannelPerformanceData();
            }, 50);
          }
        }}
      >
        <TabPane tab="Overview" key="overview" >
          <Row gutter={[16, 16]}  className="chart-row" style={{ marginBottom: 10 }}>
            {/* Sales Trend */}
            <Col xs={24} lg={17}>
              <Card>
                {loading ? (
                  <div style={{ 
                    height: getChartHeight('main'), 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Spin size="large" />
                    <div style={{ 
                      marginTop: 16, 
                      color: isFullscreen ? '#FFFFFF' : '#666666',
                      fontSize: isMonitor ? 16 : 14
                    }}>
                      {isFullscreen ? 'Loading last 4 weeks sales trend data...' : 'Loading sales trend data...'}
                    </div>
                  </div>
                ) : (
                  <ReactECharts
                    option={getSalesTrendOption()}
                    style={{ 
                      height: getChartHeight('main'),
                      width: isFullscreen ? '100%' : 'auto'
                    }}
                    opts={{ renderer: 'svg' }}
                    onChartReady={(chart) => {
                      const resizeChart = () => {
                        // Force chart resize with explicit dimensions in fullscreen
                        if (isFullscreen) {
                          setTimeout(() => chart.resize(), 50);
                        } else {
                          chart.resize();
                        }
                      };
                      window.addEventListener('resize', resizeChart);
                      
                      // Initial resize for fullscreen mode
                      if (isFullscreen) {
                        setTimeout(() => chart.resize(), 100);
                      }
                      
                      return () => window.removeEventListener('resize', resizeChart);
                    }}
                  />
                )}
              </Card>
            </Col>
            
            {/* Channel Distribution */}
            <Col xs={24} lg={7}>
              <Card>
                {loading ? (
                  <div style={{ 
                    height: getChartHeight('side'), 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Spin size="large" />
                    <div style={{ 
                      marginTop: 16, 
                      color: isFullscreen ? '#FFFFFF' : '#666666',
                      fontSize: isMonitor ? 16 : 14
                    }}>
                      {isFullscreen ? 'Loading channel performance matrix...' : 'Loading channel data...'}
                    </div>
                  </div>
                ) : (
                  <ReactECharts
                    option={getChannelDistributionOption()}
                    style={{ 
                      height: getChartHeight('side'),
                      width: isFullscreen ? '100%' : 'auto'
                    }}
                    opts={{ renderer: 'svg' }}
                    onChartReady={(chart) => {
                      const resizeChart = () => {
                        // Force chart resize with explicit dimensions in fullscreen
                        if (isFullscreen) {
                          setTimeout(() => chart.resize(), 50);
                        } else {
                          chart.resize();
                        }
                      };
                      window.addEventListener('resize', resizeChart);
                      
                      // Initial resize for fullscreen mode
                      if (isFullscreen) {
                        setTimeout(() => chart.resize(), 100);
                      }
                      
                      return () => window.removeEventListener('resize', resizeChart);
                    }}
                  />
                )}
              </Card>
            </Col>
          </Row>
          
          {/* Bottom Data Tables */}
          <Row gutter={[16, 16]} className="table-row">
            <Col xs={24} lg={12}>
              <Card title="Channel Performance Ranking">
                <Table
                  columns={[
                    {
                      title: 'Channel Name',
                      dataIndex: 'channel_name',
                      key: 'channel_name',
                    },
                    {
                      title: 'Sale-through Sales',
                      dataIndex: 'sales',
                      key: 'sales',
                      render: (value: number) => `$${(value / 10000).toFixed(0)}K`,
                    },
                    {
                      title: 'Target Achievement Rate',
                      dataIndex: 'target_rate',
                      key: 'target_rate',
                      render: (value: number) => `${value}%`,
                    },
                    {
                      title: 'Sale-through Units',
                      dataIndex: 'units',
                      key: 'units',
                      render: (value: number) => `${value.toLocaleString()}`,
                    },
                    {
                      title: 'Average Price',
                      dataIndex: 'avg_price',
                      key: 'avg_price',
                      render: (value: number) => `$${value.toLocaleString()}`,
                    },
                  ]}
                  dataSource={channelRankingData.map((item, index) => ({
                    key: index + 1,
                    channel_name: item.channel_name,
                    sales: item.sale_through_sales,
                    target_rate: item.sell_through_rate,
                    units: item.sale_through_units,
                    avg_price: item.avg_price
                  }))}
                  pagination={isFullscreen ? { pageSize: 10, showSizeChanger: false, showQuickJumper: false } : false}
                  size="small"
                  loading={channelRankingLoading}
                  scroll={isFullscreen ? { y: 'calc(100% - 39px)' } : undefined}
                />
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card title="Top 10 SKU Ranking">
                <Table
                  columns={[
                    {
                      title: 'Rank',
                      dataIndex: 'rank',
                      key: 'rank',
                    },
                    {
                      title: 'SKU Name',
                      dataIndex: 'sku_name',
                      key: 'sku_name',
                    },
                    {
                      title: 'Sale-through Sales',
                      dataIndex: 'sales',
                      key: 'sales',
                      render: (value: number) => `$${(value / 10000).toFixed(0)}K`,
                    },
                    {
                      title: 'Sale-through Units',
                      dataIndex: 'units',
                      key: 'units',
                      render: (value: number) => `${value.toLocaleString()}`,
                    },
                    {
                      title: 'Growth Rate',
                      dataIndex: 'growth_rate',
                      key: 'growth_rate',
                      render: (value: number) => {
                        const color = value > 0 ? 'green' : 'red';
                        const sign = value > 0 ? '+' : '';
                        return <span style={{ color }}>{sign}{value}%</span>;
                      },
                    },
                  ]}
                  dataSource={skuRankingData.map((item, index) => ({
                    key: index + 1,
                    rank: index + 1,
                    sku_name: item.sku_name,
                    sales: item.sale_through_sales,
                    units: item.sale_through_units,
                    growth_rate: item.growth_rate
                  }))}
                  pagination={isFullscreen ? { pageSize: 10, showSizeChanger: false, showQuickJumper: false } : false}
                  size="small"
                  loading={skuRankingLoading}
                  scroll={isFullscreen ? { y: 'calc(100% - 39px)' } : undefined}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Channel Performance" key="channel-performance">
          {/* Channel Selection Buttons */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Select Channel:</span>
              {!isFullscreen && (
                <Button 
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    console.log('Manually refreshing channel performance data');
                    fetchChannelPerformanceData();
                  }}
                  loading={channelPerformanceLoading}
                  title="Refresh Channel Data"
                >
                  Refresh
                </Button>
              )}
            </div>
            <Space wrap>
              <Button
                type={selectedChannel === 'All' ? 'primary' : 'default'}
                onClick={() => setSelectedChannel('All')}
                style={{
                  backgroundColor: selectedChannel === 'All' ? '#ff4d4f' : undefined,
                  borderColor: selectedChannel === 'All' ? '#ff4d4f' : '#d9d9d9',
                  color: selectedChannel === 'All' ? '#fff' : '#000',
                }}
              >
                All
              </Button>
              {channelPerformanceData?.channels?.map((channel: string) => (
                <Button
                  key={channel}
                  type={selectedChannel === channel ? 'primary' : 'default'}
                  onClick={() => setSelectedChannel(channel)}
                  style={{
                    backgroundColor: selectedChannel === channel ? getChannelColor(channel) : undefined,
                    borderColor: selectedChannel === channel ? getChannelColor(channel) : '#d9d9d9',
                    color: selectedChannel === channel ? '#fff' : '#000',
                  }}
                >
                  {getChannelDisplayName(channel)}
                </Button>
              )) || (
                <span style={{ color: isFullscreen ? '#CCCCCC' : '#999999', fontSize: '12px' }}>
                  Loading channels...
                </span>
              )}
            </Space>
          </div>
          
          <Row gutter={[16, 16]} className="chart-row" style={{ marginBottom: 10 }}>
            {/* Channel Performance Chart */}
            <Col xs={24}>
              <Card>
                {channelPerformanceLoading ? (
                  <div style={{ 
                    height: getChartHeight('full'), 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Spin size="large" />
                    <div style={{ 
                      marginTop: 16, 
                      color: isFullscreen ? '#FFFFFF' : '#666666',
                      fontSize: isMonitor ? 16 : 14
                    }}>
                      {isFullscreen ? 'Loading last 4 weeks channel performance...' : 'Loading channel performance...'}
                    </div>
                  </div>
                ) : (
                  <ReactECharts
                    key={`channel-performance-${selectedChannel}-${activeTab}`}
                    option={getChannelPerformanceOption()}
                    style={{ 
                      height: getChartHeight('full'),
                      width: isFullscreen ? '100%' : 'auto'
                    }}
                    opts={{ renderer: 'svg' }}
                    onChartReady={(chart) => {
                      const resizeChart = () => {
                        // Force chart resize with explicit dimensions in fullscreen
                        if (isFullscreen) {
                          setTimeout(() => chart.resize(), 50);
                        } else {
                          chart.resize();
                        }
                      };
                      window.addEventListener('resize', resizeChart);
                      
                      // Initial resize for fullscreen mode
                      if (isFullscreen) {
                        setTimeout(() => chart.resize(), 100);
                      }
                      
                      return () => window.removeEventListener('resize', resizeChart);
                    }}
                  />
                )}
              </Card>
            </Col>
          </Row>
          
          {/* Bottom Data Tables */}
          <Row gutter={[16, 16]} className="table-row">
            <Col xs={24} lg={12}>
              <Card title="Channel Performance Ranking">
                <Table
                  columns={[
                    {
                      title: 'Channel Name',
                      dataIndex: 'channel_name',
                      key: 'channel_name',
                    },
                    {
                      title: 'Sale-through Sales',
                      dataIndex: 'sales',
                      key: 'sales',
                      render: (value: number) => `$${(value / 10000).toFixed(0)}K`,
                    },
                    {
                      title: 'Target Achievement Rate',
                      dataIndex: 'target_rate',
                      key: 'target_rate',
                      render: (value: number) => `${value}%`,
                    },
                    {
                      title: 'Sale-through Units',
                      dataIndex: 'units',
                      key: 'units',
                      render: (value: number) => `${value.toLocaleString()}`,
                    },
                    {
                      title: 'Average Price',
                      dataIndex: 'avg_price',
                      key: 'avg_price',
                      render: (value: number) => `$${value.toLocaleString()}`,
                    },
                  ]}
                  dataSource={channelRankingData.map((item, index) => ({
                    key: index + 1,
                    channel_name: item.channel_name,
                    sales: item.sale_through_sales,
                    target_rate: item.sell_through_rate,
                    units: item.sale_through_units,
                    avg_price: item.avg_price
                  }))}
                  pagination={isFullscreen ? { pageSize: 10, showSizeChanger: false, showQuickJumper: false } : false}
                  size="small"
                  loading={channelRankingLoading}
                  scroll={isFullscreen ? { y: 'calc(100% - 39px)' } : undefined}
                />
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card title="Top 10 SKU Ranking">
                <Table
                  columns={[
                    {
                      title: 'Rank',
                      dataIndex: 'rank',
                      key: 'rank',
                    },
                    {
                      title: 'SKU Name',
                      dataIndex: 'sku_name',
                      key: 'sku_name',
                    },
                    {
                      title: 'Sale-through Sales',
                      dataIndex: 'sales',
                      key: 'sales',
                      render: (value: number) => `$${(value / 10000).toFixed(0)}K`,
                    },
                    {
                      title: 'Sale-through Units',
                      dataIndex: 'units',
                      key: 'units',
                      render: (value: number) => `${value.toLocaleString()}`,
                    },
                    {
                      title: 'Growth Rate',
                      dataIndex: 'growth_rate',
                      key: 'growth_rate',
                      render: (value: number) => {
                        const color = value > 0 ? 'green' : 'red';
                        const sign = value > 0 ? '+' : '';
                        return <span style={{ color }}>{sign}{value}%</span>;
                      },
                    },
                  ]}
                  dataSource={skuRankingData.map((item, index) => ({
                    key: index + 1,
                    rank: index + 1,
                    sku_name: item.sku_name,
                    sales: item.sale_through_sales,
                    units: item.sale_through_units,
                    growth_rate: item.growth_rate
                  }))}
                  pagination={isFullscreen ? { pageSize: 10, showSizeChanger: false, showQuickJumper: false } : false}
                  size="small"
                  loading={skuRankingLoading}
                  scroll={isFullscreen ? { y: 'calc(100% - 39px)' } : undefined}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Dashboard;